import { FC, MutableRefObject, useState } from 'react';
import { VideoPlayerEvents } from '@sendspark/player/dist/VideoPlayer';
import Calendar from '@components/Calendars';
import RenderIf from '@components/RenderIf';
import TranscriptSection from '@components/TranscriptPanel/TranscriptSection';
import { SvgSchedule, SvgSparkle, SvgTranscriptLines } from '@assets';
import { Campaign } from '@interfaces';
import { TranscriptSource, TRANSCRIPT_SOURCE } from '@hooks';
import { AiBadge } from '@components/TranscriptPanel/styles';
import { Body, Card, TabBar, TabBody, TabButton, TabRail } from './styles';

const TAB = {
  schedule: 'schedule',
  transcript: 'transcript',
} as const;

type Tab = (typeof TAB)[keyof typeof TAB];

interface Props {
  campaign: Campaign;
  // Player events from the share <Player> (seekTo lives here). Only used when transcript renders.
  playerEventsRef: MutableRefObject<VideoPlayerEvents | null>;
  hasCalendar: boolean;
  hasTranscript: boolean;
}

const SidePanel: FC<Props> = ({ campaign, playerEventsRef, hasCalendar, hasTranscript }) => {
  // Default to the calendar tab: it's action-oriented (booking) and must mount visible.
  // `useCreateCalendar` only inits on `calendar.link/provider` changes, not on visibility,
  // and Calendly / TidyCal size incorrectly if they boot while hidden (`display: none`).
  const [tab, setTab] = useState<Tab>(TAB.schedule);

  // Badge only the source TranscriptPanel actually loads: the AI JSON can 404 and fall back to
  // Mux captions, so we wait for the confirmed source rather than guessing from the build URL.
  const [transcriptSource, setTranscriptSource] = useState<TranscriptSource | null>(null);
  const isAiTranscript = transcriptSource === TRANSCRIPT_SOURCE.ai;

  if (!hasCalendar && !hasTranscript) return null;

  if (hasCalendar && !hasTranscript) {
    return <Calendar calendar={campaign?.calendar} />;
  }

  if (!hasCalendar && hasTranscript) {
    return <TranscriptSection campaign={campaign} playerEventsRef={playerEventsRef} />;
  }

  // Both present — render the tab switcher.
  return (
    <Card>
      <TabBar role="tablist" aria-label="Side panel">
        <TabButton
          type="button"
          role="tab"
          aria-selected={tab === TAB.schedule}
          active={tab === TAB.schedule}
          onClick={() => setTab(TAB.schedule)}
        >
          <SvgSchedule />
          <span>Schedule</span>
        </TabButton>
        <TabButton
          type="button"
          role="tab"
          aria-selected={tab === TAB.transcript}
          active={tab === TAB.transcript}
          onClick={() => setTab(TAB.transcript)}
        >
          <SvgTranscriptLines />
          <span>Transcript</span>
          <RenderIf condition={isAiTranscript}>
            <AiBadge active={tab === TAB.transcript}>
              <SvgSparkle size={10} />
              AI
            </AiBadge>
          </RenderIf>
        </TabButton>
        <TabRail index={tab === TAB.schedule ? 0 : 1} tabCount={2} />
      </TabBar>

      <Body>
        <TabBody active={tab === TAB.schedule} role="tabpanel" aria-hidden={tab !== TAB.schedule}>
          <Calendar calendar={campaign?.calendar} />
        </TabBody>
        <TabBody active={tab === TAB.transcript} role="tabpanel" aria-hidden={tab !== TAB.transcript}>
          <TranscriptSection
            campaign={campaign}
            playerEventsRef={playerEventsRef}
            embedded
            isActive={tab === TAB.transcript}
            onSourceChange={setTranscriptSource}
          />
        </TabBody>
      </Body>
    </Card>
  );
};

export default SidePanel;
